import React, { useState, useEffect } from 'react';
import { TbArrowsExchange } from "react-icons/tb";
import { RxCaretDown } from "react-icons/rx";
import './converter.css'; 
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { fetchWallets, swapCurrencyPreview, transferFundsSummary } from '../../actions/payments';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'bootstrap';
import swapIcon from '../../assets/img/icon/card_verve-card.png';
import sendIcon from '../../assets/img/icon/card_credit-card.png';
import chooseCardIcon2 from '../../assets/img/icon/card_credit-card.png';
import { toast } from 'react-toastify';
import { getAuthenticatedUser } from '../../actions/auth';
import { fetchExchangeRates } from '../../redux/slices/countriesSlice';
import { formatAmount, getCurrencyInfoByCountryCode, getCurrencyInfoByCurrencyCode, getFlagUrl } from '../../helpers/currency';


const cardOptions = [
  { id: 1, slug: 'swap', name: 'Swap Currency', description: 'Convert between currencies instantly at great rates.', icon: swapIcon },
  { id: 2, slug: 'send', name: 'Send Money', description: 'Send funds locally or internationally with ease.', icon: sendIcon },
];
const selectedTransferLocation = { id: 2, slug: "international", name: "International Transfer", description: "Easily transfer money across borders with competitive rates and global reach.", icon: chooseCardIcon2 }

const CurrencyConverter = ({ sendMoneyPage = false, payload, txnAmountDetails, setIsTxnDetailsEdited, setTxnAmountDetails }) => {
  const userDetails = useSelector((state) => state.user.user);
  const location = useLocation();
  const backToCalDefault = location.state?.backToCalDefault;
  const dashboardBaseUrl = process.env.REACT_APP_DASHBOARD_BASE_URL;
  const { currencyOptions, conversionRates } = useSelector((state) => state.country);

  const isBankTransfer = payload?.transfer_type === 'bank';
  const isSendMoneyPage = location.pathname.includes('user/send_money');
  
  const senderCurrencyCodeFromPayload = payload?.fundingSource?.currency_code;
  const [senderCountry, setSenderCountry] = useState(null);
  const [sendCurrency, setSendCurrency] = useState(senderCurrencyCodeFromPayload);
  
  const recipientCountryCode = payload?.recipient?.recipientCountry?.toUpperCase();
  const payloadReceivingCurrencyCode = payload?.recipient?.recipientCurrencyCode;
  const recipientCurrencyCode = payloadReceivingCurrencyCode || getCurrencyInfoByCountryCode(currencyOptions, recipientCountryCode)?.currencyCode;
  const [receiverCountry, setReceiverCountry] = useState(null);
  const [receiveCurrency, setReceiveCurrency] = useState(recipientCurrencyCode);

  const [moneyExchangeDestination, setMoneyExchangeDestination] = useState(null);
  
  // const localTransferType = payload?.location?.slug === "local" ? true : false;
  const localTransferType = sendCurrency == receiveCurrency;

  const [sendAmount, setSendAmount] = useState(0);
  const [receiveAmount, setReceiveAmount] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(0);
  
  const [showSendDropdown, setShowSendDropdown] = useState(false);
  const [showReceiveDropdown, setShowReceiveDropdown] = useState(false);
  
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState(null);
  const [userWallets, setUserWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [txnAmountInfo, setTxnAmountInfo] = useState();
  const [lastEdited, setLastEdited] = useState('send'); // or 'receive'

  useEffect(() => {
    if (isSendMoneyPage && sendCurrency && receiveCurrency) {
      const transactionDetailsInfo = {
        sendCurrency,
        receiveCurrency,
        sendAmount,
        receiveAmount
      };
      setTxnAmountDetails(transactionDetailsInfo);
    }
  }, [sendCurrency, receiveCurrency, sendAmount, receiveAmount]);

  const fetchAllWallets = async () => {
      setLoading(true);
      try {
        const response = await fetchWallets();
        const wallets = response.data.wallets.wallets;
        setUserWallets(wallets);
      } catch (error) {
        // console.error("Error fetching wallets:", error);
      } finally {
        setLoading(false);
      }
  };
  
  useEffect(() => {
    fetchAllWallets();
    dispatch(fetchExchangeRates());
  }, []);

  useEffect(() => {
    if (txnAmountDetails) {
      setSendCurrency(sendCurrency || txnAmountDetails.sendCurrency);
      setReceiveCurrency(receiveCurrency || txnAmountDetails.receiveCurrency);
      setSendAmount(txnAmountDetails.sendAmount);
      setReceiveAmount(txnAmountDetails.receiveAmount);
    }
  }, []);

  // display converter in default mode
  useEffect(() => {
    if (backToCalDefault) {
      setSendCurrency(sendCurrency || backToCalDefault.sendCurrency);
      setReceiveCurrency(receiveCurrency || backToCalDefault.receiveCurrency);
      setSendAmount(backToCalDefault.sendAmount);
      setReceiveAmount(backToCalDefault.receiveAmount);
    }
  }, []);

  const findRate = (from, to) => {
    if (from === to) return 1;
    
    const fromData = conversionRates.find(r => r.currency_code === from);
    const toData = conversionRates.find(r => r.currency_code === to);
    if (!fromData || !toData) return 0;
    
    const fromBuyRate = parseFloat(fromData.buy_rate);
    const toSellRate = parseFloat(toData.sell_rate);
    return toSellRate / fromBuyRate;
  };

  useEffect(() => {
    if (sendCurrency && receiveCurrency && conversionRates?.length > 0) {
      const rate = findRate(sendCurrency, receiveCurrency);
      if (rate === 0) {
        toast.error(`No conversion rate found for ${sendCurrency} to ${receiveCurrency}`);
        setExchangeRate(0);
        setReceiveAmount("0.00");
        return;
      }

      setExchangeRate(
        rate > 0.1 ? rate.toFixed(4) :
        rate > 0.01 ? rate.toFixed(6) : 
        rate.toFixed(8)
      );
      if (lastEdited === 'send') {
        const receiveAmountInCents = Math.floor((sendAmount * rate) * 100);
        setReceiveAmount(receiveAmountInCents/100);
      } else if (lastEdited === 'receive') {
        const sendingAmountInCents = Math.ceil(receiveAmount * 100/rate);
        setSendAmount(sendingAmountInCents/100);
      }
    }
  }, [sendCurrency, receiveCurrency, sendAmount, receiveAmount, conversionRates, lastEdited]);

  const handleCurrencySwapBtn = () => {
    setSendCurrency(prev => {
      setReceiveCurrency(prev);
      return receiveCurrency;
    });
  };

  const handleSendMoneyOptionClick = (card) => {
    const destinationUri = card.slug == 'swap'  
      ? '/user/converter'
      : '/user/send_money'
    setMoneyExchangeDestination(dashboardBaseUrl + destinationUri);
    setSelectedOption(card.slug)
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSendDropdown || showReceiveDropdown) {
        if (!event.target.closest('.currency-selector')) {
          setShowSendDropdown(false);
          setShowReceiveDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSendDropdown, showReceiveDropdown]);

  
  useEffect(() => {
    const userCurrencyCode = getCurrencyInfoByCountryCode(currencyOptions, userDetails?.country_code)?.currencyCode;
    const senderCurrencyCode = payload?.fundingSource?.currency_code || userCurrencyCode || 'NGN';
    setSenderCountry(getCurrencyInfoByCurrencyCode(currencyOptions, senderCurrencyCode));
    setSendCurrency(senderCurrencyCode);

    const defaultReceivingCurrency = senderCurrencyCode != 'MWK' ? 'MWK' : 'NGN';
    const recipientCountryCode = payload?.recipient?.recipientCountry?.toUpperCase();
    const recipientCurrencyCode = getCurrencyInfoByCountryCode(currencyOptions, recipientCountryCode)?.currencyCode || defaultReceivingCurrency;
    setReceiverCountry(getCurrencyInfoByCurrencyCode(currencyOptions, recipientCurrencyCode));
    setReceiveCurrency(recipientCurrencyCode);
  }, [currencyOptions])

  useEffect(() => {
    setSenderCountry(getCurrencyInfoByCurrencyCode(currencyOptions, sendCurrency));
    setReceiverCountry(getCurrencyInfoByCurrencyCode(currencyOptions, receiveCurrency));
  }, [sendCurrency, receiveCurrency])

  return (
    <div className={`currency-exchange-container ${sendMoneyPage ? 'send-money-page' : ''}`}>
      <div className="exchange-header">
        <div className="d-flex justify-content-between mb-2">
          <span className="text-muted small">You send exactly</span>
          {(!localTransferType || !isSendMoneyPage) && <span className="text-muted small">Recipient receives</span>}
        </div>
        
        <div className="exchange-input-container" style={{ gridTemplateColumns : (localTransferType && isSendMoneyPage) ? '1fr' : '49% 49%' }}>
          {/* Send Currency Section */}
          <div className="currency-field send-field">
            <div className="currency-selector">
              <button className="currency-select-btn" onClick={() => setShowSendDropdown(!showSendDropdown)}>
                <img src={getFlagUrl(senderCountry?.countryCode)}  alt=""  className="currency-flag" />
                <span className="currency-code">{currencyOptions.length > 0 && sendCurrency}</span>
                {!isSendMoneyPage && <RxCaretDown color='#0A95FF' />}
              </button>
              {showSendDropdown && (
                <div className="currency-dropdown">
                  {currencyOptions?.map(currency => (
                    <div 
                      key={currency.currencyCode} 
                      className={`currency-option ${currency.currencyCode === sendCurrency ? 'selected' : ''}`}
                      onClick={() => {
                        setSendCurrency(currency.currencyCode);
                        setShowSendDropdown(false);
                      }}
                    >
                      <img src={currency.flagUrl} alt="" className="currency-flag" />
                      <span>{currency.country}</span>
                      <span>{currency.currencyCode}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <input
              type="text"
              value={sendAmount.toLocaleString()}
              onChange={(e) => {
                const value = e.target.value.replace(/,/g, '');
                if (!isNaN(value) && value !== '') {
                  setLastEdited('send');
                  setSendAmount(parseFloat(value));
                } else if (value === '') {
                  setSendAmount(0);
                }
              }}
              className="amount-input"
            />
          </div>
          
          {/* Swap Button */}
          {/* {!isSendMoneyPage && (
            <div className="swap-button" onClick={handleCurrencySwapBtn}>
              <i className="swap-icon"><TbArrowsExchange size={20} /></i>
            </div>
          )} */}
          
          {/* Receive Currency Section */}
          {(!localTransferType || !isSendMoneyPage) && (
            <div className="currency-field receive-field">
              <div className="currency-selector">
                <button 
                  className="currency-select-btn"
                  onClick={() => setShowReceiveDropdown(!showReceiveDropdown)}
                  disabled={!localTransferType && isSendMoneyPage}
                >
                  <img src={getFlagUrl(receiverCountry?.countryCode)} alt="" className="currency-flag"/>
                  <span className="currency-code">{currencyOptions.length > 0 && receiveCurrency}</span>
                  {(!localTransferType && !isSendMoneyPage) && <RxCaretDown color='#0A95FF' />}
                </button>
                
                {showReceiveDropdown && (
                  <div className="currency-dropdown">
                    {((localTransferType || !isSendMoneyPage) ? currencyOptions : currencyOptions?.filter(currency => currency.currencyCode !== sendCurrency)).map(currency => (
                      <div 
                        key={currency.currencyCode} 
                        className={`currency-option ${currency.currencyCode === receiveCurrency ? 'selected' : ''}`}
                        onClick={() => {
                          setReceiveCurrency(currency.currencyCode);
                          setShowReceiveDropdown(false);
                        }}
                      >
                          <img src={currency.flagUrl} alt="" className="currency-flag" />
                          <span>{currency.country}</span>
                          <span>{currency.currencyCode}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <input
                type="text"
                value={receiveAmount.toLocaleString()}
                onChange={(e) => {
                  const value = e.target.value.replace(/,/g, '');
                  if (!isNaN(value) && value !== '') {
                    setLastEdited('receive');
                    setReceiveAmount(parseFloat(value));
                  } else if (value === '') {
                    setReceiveAmount(0);
                  }
                }}
                className="amount-input"
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Exchange Information */}
      <div className="exchange-details">
        {!localTransferType && (
          <div className="detail-row">
            <span className="detail-label">Exchange rate</span>
            <span className="detail-value">
              {senderCountry?.symbol}1.00 ≈ {receiverCountry?.symbol}{exchangeRate}
            </span>
          </div>
        )}
        
        <div className="detail-row">
          <span className="detail-label">You send exactly</span>
          <span className="detail-value">
            {senderCountry?.symbol}{formatAmount(sendAmount)}
          </span>
        </div>
                
        <div className="detail-row">
          <span className="detail-label">Recipient will receive</span>
          <span className="detail-value">
            {receiverCountry?.symbol}{formatAmount(receiveAmount)}
          </span>
        </div>
      </div>
      
      {/* Send Money Button */}
      <button type="button" onClick={() => {
        const modalElement = document.getElementById('selectActionCardModal');
        const bsModal = Modal.getInstance(modalElement) || new Modal(modalElement);
        bsModal.show();
      }}
      className='primary__btn send-money-btn' href={`${dashboardBaseUrl}/login`} target="_blank" rel="noopener noreferrer">
        Send Money
      </button>

      {/* <!-- Modal --> */}
      <section className="modal fade" id="selectActionCardModal" tabIndex="-1" aria-labelledby="selectActionCardModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                  <div className="d-flex justify-content-end pe-3 pt-3">
                      <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                  </div>
                  <div className="modal-body mb-5 mx-4">
                    <h4 className="text-center mb-5">What would you like to do?</h4>

                    <div className="card-selection-container">
                      {cardOptions.map((card) => (
                        <button
                          key={card.id}
                          className={`card-option ${selectedOption === card.slug ? "active" : ""}`}
                          onClick={() => handleSendMoneyOptionClick(card)}
                        >
                          <img src={card.icon} alt={card.name} className="card-icon mt-2" width="40px" height="40px" />
                          <h5 className="card-title">{card.name}</h5>
                          <p className="card-description">{card.description}</p>
                          <div className="radio-button">
                            <div className={`radio-dot ${selectedOption === card.slug ? "checked" : ""}`} />
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="d-flex align-items-center gap-3 justify-content-between mt-5">
                      <button className="btn primary__btn__outline w-50" data-bs-dismiss="modal" aria-label="Close">Cancel</button>
                      {moneyExchangeDestination && <a className="btn primary__btn w-50" href={moneyExchangeDestination} target='__blank'>
                        Continue
                      </a>}
                    </div>
                </div>
              </div>
          </div>
      </section>
      
      {sendCurrency != receiveCurrency &&
        <p className="disclaimer-text">
          Exchange rates are dynamic. Always remember to check the applicable rate in-app before carrying out your exchange.
        </p>
      }
    </div>
  );
};

export default CurrencyConverter;